import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiResponse } from '../interfaces/api-response.interface';
import { AdminNotification } from '../interfaces/notification.interface';

/**
 * Notifications live in a schema-less collection, so this service talks to
 * API Maker's generic (`/api/gen`) endpoint rather than the schema-validated
 * (`/api/schema`) one the rest of the admin uses.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsApiService {
    private readonly http = inject(HttpClient);
    private readonly col = 'notifications';

    readonly notifications = signal<AdminNotification[]>([]);
    readonly unreadCount = computed(() => this.notifications().filter(n => !n.is_read).length);

    private pollTimer: any = null;

    private genUrl(path: string = ''): string {
        return `${environment.T_SHIRT_CAFE_BE_HOST_PORT}/api/gen/${environment.USER_PATH}/${environment.INS}/${environment.DB}/${this.col}${path}`;
    }

    async fetchLatest(limit = 20): Promise<void> {
        try {
            const res = await firstValueFrom(
                this.http.post<ApiResponse<AdminNotification[]>>(
                    `${this.genUrl('/query')}`,
                    { find: {}, sort: { createdAt: -1 }, limit },
                ),
            );
            this.notifications.set(res?.data ?? []);
        } catch {
            // swallow — next poll retries
        }
    }

    async markRead(id: string): Promise<void> {
        await firstValueFrom(
            this.http.put<ApiResponse<AdminNotification>>(
                `${this.genUrl('/update-by-id/' + encodeURIComponent(id))}`,
                { is_read: true, updatedAt: new Date().toISOString() },
            ),
        );
        this.notifications.update(list => list.map(n => n._id === id ? { ...n, is_read: true } : n));
    }

    async markAllRead(): Promise<void> {
        const unread = this.notifications().filter(n => !n.is_read);
        await Promise.all(unread.map(n => this.markRead(n._id)));
    }

    startPolling(intervalMs = 15000): void {
        if (this.pollTimer) return;
        this.fetchLatest().catch(() => void 0);
        this.pollTimer = setInterval(() => this.fetchLatest().catch(() => void 0), intervalMs);
    }

    stopPolling(): void {
        if (!this.pollTimer) return;
        clearInterval(this.pollTimer);
        this.pollTimer = null;
    }
}
